import React, { useState } from 'react';
import { LogIn, Shield, User, Lock, ExternalLink } from 'lucide-react';
import { authAPI } from '../services/api';
import { useTranslation } from 'react-i18next';

const Login = ({ onSuccess }) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const response = await authAPI.login({ username, password });
      const authData = response?.data;

      if (!authData?.token) throw new Error('Invalid response');

      localStorage.setItem('authToken', authData.token);
      localStorage.setItem('userRole', authData.user?.role || 'user');
      localStorage.setItem('userName', authData.user?.username || '');
      onSuccess?.(authData.user);
    } catch (err) {
      setError(err?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8fafc] relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-100 rounded-full blur-[120px] opacity-50"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-100 rounded-full blur-[120px] opacity-50"></div>

      <div className="w-full max-w-[1000px] bg-white shadow-[0_20px_50px_rgba(0,0,0,0.1)] rounded-3xl overflow-hidden grid grid-cols-1 md:grid-cols-2 m-4 relative z-10">
        {/* Left Side: Visual/Branding Panel */}
        <div className="hidden md:flex flex-col justify-between bg-[#1e293b] text-white p-12 relative overflow-hidden">
          {/* Subtle mesh gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-transparent"></div>

          <div className="relative z-10">
            <div className="flex items-center space-x-2 mb-12">
              <div className="bg-blue-500 p-2 rounded-xl">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight">BAS System</span>
            </div>

            <h2 className="text-4xl font-extrabold leading-tight mb-6">
              Empowering <br />
              <span className="text-blue-400">Connectivity</span> & <br />
              Compliance.
            </h2>
            <p className="text-slate-400 text-lg max-w-xs leading-relaxed">
              The central hub for telecom billing and security fee management.
            </p>
          </div>

          <div className="relative z-10">
            <div className="space-y-4">
              <div className="flex items-center space-x-3 text-sm text-slate-300">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                <span>Real-time billing analytics</span>
              </div>
              <div className="flex items-center space-x-3 text-sm text-slate-300">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                <span>Operator compliance tracking</span>
              </div>
              <div className="flex items-center space-x-3 text-sm text-slate-300">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                <span>Automated reporting & audit logs</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Login Form */}
        <div className="p-10 md:p-16 flex flex-col justify-center">
          <div className="mb-10">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">{t('login.title')}</h1>
            <p className="text-slate-500">Welcome back. Please enter your details.</p>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm flex items-center">
              <AlertTriangle className="h-4 w-4 mr-2 shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-semibold text-slate-700 mb-2">
                {t('login.username')}
              </label>
              <div className="relative group">
                <User className="h-5 w-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 group-focus-within:text-blue-500 transition-colors" />
                <input
                  id="username"
                  name="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-slate-900"
                  placeholder={t('login.usernamePlaceholder')}
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-slate-700 mb-2">
                {t('login.password')}
              </label>
              <div className="relative group">
                <Lock className="h-5 w-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 group-focus-within:text-blue-500 transition-colors" />
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-slate-900"
                  placeholder={t('login.passwordPlaceholder')}
                  required
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <span className="ml-2 text-sm text-slate-600">{t('login.rememberMe') || 'Keep me signed in'}</span>
              </label>
              <button type="button" className="text-sm font-semibold text-blue-600 hover:text-blue-700">
                {t('login.forgotPassword') || 'Reset password'}
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full flex items-center justify-center space-x-2 py-4 rounded-xl font-bold text-white shadow-lg transition-all active:scale-[0.98] ${loading
                  ? 'bg-slate-300 cursor-not-allowed shadow-none'
                  : 'bg-blue-600 hover:bg-blue-700 shadow-blue-500/25 hover:shadow-blue-500/40'
                }`}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  <LogIn className="h-5 w-5" />
                  <span>{t('login.signIn')}</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-10 pt-8 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
            <span>© {new Date().getFullYear()} BAS System</span>
            <div className="flex space-x-4">
              <button className="hover:text-slate-600 flex items-center">
                Privacy Policy <ExternalLink className="h-3 w-3 ml-1" />
              </button>
              <button className="hover:text-slate-600 flex items-center">
                Terms of Use <ExternalLink className="h-3 w-3 ml-1" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Mock AlertTriangle for the error message since it wasn't imported initially
const AlertTriangle = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" />
  </svg>
);

export default Login;
